import { useEffect, useRef } from 'react';
import { useBlocker } from 'react-router-dom';

const DEFAULT_MESSAGE = 'You have unsaved changes. Leave without saving?';

type BlockerLike = {
    state: string;
    proceed?: () => void;
    reset?: () => void;
};

/**
 * Warns before leaving when `when` is true.
 *
 * - Data router (createBrowserRouter): uses useBlocker for all in-app navigations.
 * - Classic BrowserRouter: intercepts same-origin link clicks (sidebar NavLinks, etc.).
 * - Both: beforeunload for tab close / refresh.
 */
export const useUnsavedChangesWarning = (
    when: boolean,
    message: string = DEFAULT_MESSAGE,
): void => {
    const messageRef = useRef(message);
    messageRef.current = message;

    let blocker: BlockerLike;
    let hasDataRouterBlocker = true;
    try {
        blocker = useBlocker(
            ({ currentLocation, nextLocation }) =>
                when && currentLocation.pathname !== nextLocation.pathname,
        );
    } catch {
        hasDataRouterBlocker = false;
        blocker = { state: 'unblocked', proceed: () => {}, reset: () => {} };
    }

    useEffect(() => {
        if (blocker.state === 'blocked') {
            if (window.confirm(message)) {
                blocker.proceed?.();
            } else {
                blocker.reset?.();
            }
        }
    }, [blocker, message]);

    // Classic BrowserRouter fallback: block same-origin <a> navigations (e.g. BaseNavLink).
    useEffect(() => {
        if (!when || hasDataRouterBlocker) {
            return;
        }

        const onClick = (event: MouseEvent) => {
            if (event.defaultPrevented || event.button !== 0) {
                return;
            }
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
                return;
            }

            const anchor = (event.target as Element | null)?.closest?.('a[href]') as
                | HTMLAnchorElement
                | null;
            if (!anchor) {
                return;
            }
            if (anchor.target && anchor.target !== '_self') {
                return;
            }
            if (anchor.hasAttribute('download')) {
                return;
            }

            const href = anchor.getAttribute('href');
            if (!href || href.startsWith('#')) {
                return;
            }

            let url: URL;
            try {
                url = new URL(href, window.location.href);
            } catch {
                return;
            }

            if (url.origin !== window.location.origin) {
                return;
            }
            if (url.pathname === window.location.pathname) {
                return;
            }

            if (!window.confirm(messageRef.current)) {
                event.preventDefault();
                event.stopPropagation();
            }
        };

        document.addEventListener('click', onClick, true);
        return () => document.removeEventListener('click', onClick, true);
    }, [when, hasDataRouterBlocker]);

    useEffect(() => {
        if (!when) {
            return;
        }

        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = messageRef.current;
            return messageRef.current;
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [when]);
};
